import { Space } from "antd";
import { Component } from "react";
import "react-dom";
import { type RouteComponentProps } from "react-router-dom";

import { UpgradeRequiredError } from "utilities/apiHandler";
import { lazyLoadWithRetry } from "utilities/react.utilities";

import { BTButtonDeprecated } from "commonComponents/btWrappers/BTButtonDeprecated/BTButtonDeprecated";
import {
    BTConfirm,
    BTConfirmContent,
    BTConfirmFooter, // BTConfirmHeader,
} from "commonComponents/btWrappers/BTConfirm/BTConfirm";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTSuspense } from "commonComponents/btWrappers/BTSuspense/BTSuspense";

const SubscriptionManager = lazyLoadWithRetry(
    () => import("entity/SubscriptionManager/SubscriptionManager"),
);

interface IUpgradeRequiredProps extends RouteComponentProps<any> {
    modalConfig?: IModalConfiguration;
    children?: React.ReactNode;
}

interface IUpgradeRequiredState {
    error?: UpgradeRequiredError;
    displaySubscriptionManager: boolean;
}

/**
 * Display an upgrade prompts alongside the subscription manager whenever an {@link UpgradeRequiredError} is thrown to the error boundary
 * @see withUpgradeRequiredBoundary
 * @see https://reactjs.org/docs/error-boundaries.html
 * @example:
 * <UpgradeRequiredBoundary>
 *  <ComponentThatMightThrowException />
 * </UpgradeRequiredBoundary>
 */
export class UpgradeRequiredBoundary extends Component<
    IUpgradeRequiredProps,
    IUpgradeRequiredState
> {
    state: Readonly<IUpgradeRequiredState> = {
        displaySubscriptionManager: false,
    };

    componentDidCatch(error: Error) {
        if (error instanceof UpgradeRequiredError) {
            this.setState({
                error,
            });
        } else {
            throw error;
        }
    }

    render() {
        const { error, displaySubscriptionManager } = this.state;
        const { children } = this.props;
        return (
            <>
                {displaySubscriptionManager && (
                    <BTSuspense>
                        <SubscriptionManager
                            history={this.props.history}
                            location={this.props.location}
                            match={this.props.match}
                            fromSettings={false}
                            fromFrozenBuilder={false}
                            modalConfig={{
                                parentRoute:
                                    this.props.modalConfig?.parentRoute ??
                                    this.props.history.location.pathname,
                                beforeClose: () => {
                                    this.setState({
                                        error: undefined,
                                        displaySubscriptionManager: false,
                                    });

                                    window.top!.location.assign(window.top!.location.href);
                                    this.props.modalConfig?.beforeClose();
                                },
                            }}
                        />
                    </BTSuspense>
                )}

                {!error && children}
                {!displaySubscriptionManager && error && (
                    <BTConfirm
                        icon="warning"
                        title="Upgrade Required"
                        data-testid="upgradeRequiredPrompt"
                        visible
                    >
                        <BTConfirmContent>{error.message}</BTConfirmContent>
                        <BTConfirmFooter>
                            <Space size="small">
                                <BTButtonDeprecated
                                    id="cancelUpgrade"
                                    data-testid="cancelUpgrade"
                                    type="secondary"
                                    onClick={() => {
                                        this.setState({ error: undefined });
                                        this.props.modalConfig?.beforeClose();
                                    }}
                                >
                                    {error.userCanUpgrade ? "Cancel" : "OK"}
                                </BTButtonDeprecated>
                                {error.userCanUpgrade && (
                                    <BTButtonDeprecated
                                        id="upgradeNow"
                                        data-testid="upgradeNow"
                                        type="new"
                                        onClick={() => {
                                            this.setState({ displaySubscriptionManager: true });
                                        }}
                                    >
                                        Upgrade
                                    </BTButtonDeprecated>
                                )}
                            </Space>
                        </BTConfirmFooter>
                    </BTConfirm>
                )}
            </>
        );
    }
}

/**
 * Wraps a component with a UpgradeRequiredBoundary, this will display an upgrade message if the components or it's children throw {@link UpgradeRequiredError}
 * @example:
 * export MyRequiresSpecificFeatureOrThresholdComponent = withUpgradeRequiredBoundary(ComponentThatMightRequireAnUpgrade)();
 */
export const withUpgradeRequiredBoundary = <P extends RouteComponentProps>(
    Component: React.ComponentType<P & IUpgradeRequiredProps>,
) => {
    return function () {
        const Wrapped = (props: P & IUpgradeRequiredProps) => {
            return (
                <UpgradeRequiredBoundary {...props}>
                    <Component {...props} />
                </UpgradeRequiredBoundary>
            );
        };

        // Format for display in DevTools
        Wrapped.displayName = Component.name
            ? `WithUpgradeRequiredBoundary(${Component.name})`
            : "WithUpgradeRequiredBoundary";

        return Wrapped;
    };
};
